import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

// T12.c: URL = the single source of truth for every listing (shareable, back button works).
export const LISTING_KEYS = ["sort", "page", "size", "q", "cursor"];
export const SPEC_KEYS = ["cpu", "ram", "storage", "screen", "resolution", "panel", "refresh", "os"];

export function useListing(fetcher, deps = []) {
  const [sp, setSp] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const seq = useRef(0);
  const key = sp.toString();
  useEffect(() => {
    const n = ++seq.current;
    setLoading(true);
    const params = Object.fromEntries(sp.entries());
    fetcher(params)
      .then((d) => { if (n === seq.current) { setData(d); setError(false); } })
      .catch(() => { if (n === seq.current) setError(true); })
      .finally(() => { if (n === seq.current) setLoading(false); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, ...deps]);

  const api = useMemo(() => {
    const commit = (fn, keepPage = false) => {
      const n = new URLSearchParams(sp);
      fn(n);
      n.delete("cursor");
      if (!keepPage) n.delete("page");
      setSp(n);
    };
    const list = (k) => (sp.get(k) || "").split(",").map((x) => x.trim()).filter(Boolean);
    return {
      sp, list,
      toggle: (k, v) => commit((n) => {
        const cur = list(k);
        const next = cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v];
        if (next.length) n.set(k, next.join(",")); else n.delete(k);
        if (k === "avail") n.delete("in_stock");
      }),
      set: (k, v, keepPage = false) => commit((n) => { if (v == null || v === "") n.delete(k); else n.set(k, v); }, keepPage),
      setPrice: (min, max) => commit((n) => {
        if (min != null && min !== "") n.set("price_min", min); else n.delete("price_min");
        if (max != null && max !== "") n.set("price_max", max); else n.delete("price_max");
      }),
      remove: (keys) => commit((n) => keys.forEach((k) => n.delete(k))),
      clearAll: () => commit((n) => [...n.keys()].filter((k) => !["q", "sort", "size"].includes(k)).forEach((k) => n.delete(k))),
    };
  }, [sp, setSp]);
  return { data, loading, error, ...api };
}
